import type { TileKind } from "./tiles";

/**
 * Days and times for the ride card: the half-hour times its time lists
 * offer, where each list starts, and the day arithmetic.
 */

function hhmm(m: number): string {
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

/**
 * Every half hour of the day, starting at `from` ("HH:MM") and wrapping past
 * midnight: 48 times. A `keep` time that is not on the half hour (a table
 * booked for 19:45) is slotted in where it falls, so it stays choosable.
 */
export function timeOptions(from: string, keep?: string): string[] {
  const start = minutesOf(from) ?? 0;
  const out = Array.from({ length: 48 }, (_, i) => hhmm((start + i * 30) % (24 * 60)));
  const k = keep ? minutesOf(keep) : null;
  if (keep && k !== null && k < 24 * 60 && !out.includes(keep)) {
    const offset = (m: number) => (m - start + 24 * 60) % (24 * 60);
    const at = out.findIndex((x) => offset(minutesOf(x)!) > offset(k));
    out.splice(at === -1 ? out.length : at, 0, keep);
  }
  return out;
}

/**
 * Where the pick-up list starts: evening rides (a restaurant, a night out)
 * from 18:00 so the likely times come first; the rest from 04:00, the first
 * flights.
 */
export function pickupListStart(kind: TileKind): string {
  return kind === "restaurant" || kind === "night" ? "18:00" : "04:00";
}

/** Where the ride-back list starts, per ride. */
export function returnListStart(kind: TileKind): string {
  switch (kind) {
    case "city":
      return "12:00";
    case "night":
      return "00:00";
    default:
      // Restaurant and the Agafay dinner.
      return "20:00";
  }
}

/**
 * Once the pick-up is chosen, the ride-back list starts half an hour after it
 * and runs round the clock, so the times read in the order they happen and
 * the ones after midnight carry "next day".
 */
export function returnListStartAfter(pickup: string): string {
  const m = minutesOf(pickup);
  return m === null ? "00:00" : hhmm((m + 30) % (24 * 60));
}

/** "HH:MM" to minutes past midnight, or null. */
export function minutesOf(time: string): number | null {
  const m = /^(\d{2}):(\d{2})$/.exec(time);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** Minutes past midnight now, on Casablanca's clock (the server's clock for a pick-up). */
export function casablancaNowMinutes(now: Date = new Date()): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Africa/Casablanca",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(now);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return get("hour") * 60 + get("minute");
}

/** A YYYY-MM-DD `n` days after `iso`. */
export function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(Date.UTC(y!, m! - 1, d! + n));
  return date.toISOString().slice(0, 10);
}

/** True for a real calendar day written YYYY-MM-DD. */
export function isIsoDay(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  return addDays(value, 0) === value;
}

function format(iso: string, lang: string, options: Intl.DateTimeFormatOptions): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Intl.DateTimeFormat(`${lang}-u-nu-latn`, { ...options, timeZone: "UTC" }).format(new Date(Date.UTC(y, m - 1, d)));
}

/** "Thu 16 Oct" in the page's language, Latin digits. */
export function formatDay(iso: string, lang: string): string {
  return format(iso, lang, { weekday: "short", day: "numeric", month: "short" });
}

/** "Mon 13" in the page's language, Latin digits: a day chip's label. Weekday first in every language. */
export function formatChipDay(iso: string, lang: string): string {
  return `${format(iso, lang, { weekday: "short" })} ${format(iso, lang, { day: "numeric" })}`;
}

/** `count` days in a row from `from`, YYYY-MM-DD. */
export function daysFrom(from: string, count: number): string[] {
  return Array.from({ length: count }, (_, i) => addDays(from, i));
}
